import { useEffect, useMemo, useState, type FC, type JSX } from 'react';

import {
  Drawer,
  Container,
  Row,
  Col,
  Input,
  PhoneInputField,
  TextArea,
  Select,
  Form,
  toast,
  getStoreValue,
} from 'pixel-react';
import useGeoLocation from 'react-ipgeolocation';
import { useSelector } from 'react-redux';

import { RootState } from '@/store';
import { Test_Optimize_ResponseObject } from '@/store/commonApiType/testOptimizeUserType';
import {
  useLazyGetUserProfileDetailsQuery,
  useUpdateLicenseFormMutation,
} from '@/store/userProfile/userProfileSlice';

import { optionsList, UPGRADE_FORM_VALIDATION, TopicList } from './constants';
import type { FormValues, UpgradeDrawerProps } from './type';
import styles from './Upgrade.module.scss';
export const UpgradeDrawer: FC<UpgradeDrawerProps> = ({
  closeUpgradeDrawer,
}): JSX.Element => {
  const clientName = import.meta.env.VITE_CLIENT_DETAIL?.name;
  const profileData = useSelector(
    (state: RootState) => state.profileDataSlice.profileData,
  );
  const [countryCode, setCountryCode] = useState<string>('IN');
  const location = useGeoLocation();
  const [updateLicenseForm, { isLoading: isUpdating }] =
    useUpdateLicenseFormMutation();
  const userInfo = useMemo(
    () => profileData?.responseObject || {},
    [profileData],
  );
  const [userProfileDataRefetch] = useLazyGetUserProfileDetailsQuery();

  const userDetails = getStoreValue(
    'test-optimize-user',
  ) as Test_Optimize_ResponseObject;
  const { licenseType, id } = userDetails?.responseObject ?? {};
  const initialFormState: FormValues = {
    userName: userInfo.name,
    email: userInfo.emailId,
    phoneNumber: userInfo.phoneNumbers?.primaryPhone,
    organizationName: userInfo.organisationName,
    jobTitle: userInfo.jobTitle,
    others: userInfo.jobTitleOthers,
    message: '',
    topic: licenseType === 'C-Basic' ? 'License Upgradation' : 'Token Renewal',
  };
  const [isOtherJobTitle, setIsOtherJobTitle] = useState<boolean>(false);

  useEffect(() => {
    if (location?.country) {
      setCountryCode(location?.country);
    }
    if (userInfo.jobTitle === 'Others') {
      setIsOtherJobTitle(true);
    }
  }, [location?.country, userInfo]);

  const handleSubmitForm = (data: FormValues) => {
    const payload = {
      name: data.userName,
      emailId: data.email,
      organisationName: data.organizationName,
      jobTitle: data.jobTitle,
      jobTitleOthers: data.others,
      message: data.message,
      phoneNumber: data.phoneNumber,
      licenseActionType: data?.topic,
    };

    updateLicenseForm({ payload })
      .unwrap()
      .then(response => {
        if (response.responseCode === 200) {
          userProfileDataRefetch(id);
          closeUpgradeDrawer();
          toast.success(response.message ?? '');
        }
      })
      .catch((error: { message: string }) => {
        closeUpgradeDrawer();
        toast.error(error.message);
      });
  };

  return (
    <Form<FormValues>
      onSubmit={handleSubmitForm}
      defaultValues={initialFormState}
      mode="onChange"
      id="upgradeForm"
    >
      {({
        register,
        setValue,
        watch,
        trigger,
        handleSubmit,
        formState: { errors, isValid, isDirty },
      }) => {
        return (
          <Drawer
            isOpen
            title={`Fill Out The Form To Hear From Team ${clientName}!`}
            overlay
            size="medium"
            isFooterRequired
            onClose={() => closeUpgradeDrawer()}
            primaryButtonProps={{
              label: 'Send',
              type: 'submit' as const,
              form: 'upgradeForm',
              disabled: !isValid || !isDirty || isUpdating,
              onClick:
                isValid && isDirty && !isUpdating
                  ? handleSubmit(handleSubmitForm)
                  : () => {},
            }}
            secondaryButtonProps={{
              label: 'Cancel',
              onClick: () => closeUpgradeDrawer(),
            }}
            zIndex={3000}
          >
            <Container fluid gap="16px" className={styles.upgrade_container}>
              <Row gap="16px">
                <Col size={6}>
                  <Input
                    label="Name"
                    id="userName"
                    placeholder="Enter your Name"
                    variant="primary"
                    type="text"
                    autoComplete="off"
                    required
                    value={watch('userName')}
                    {...register('userName', UPGRADE_FORM_VALIDATION.userName)}
                    onChange={e => {
                      setValue('userName', e.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    onBlur={() => {
                      trigger('userName');
                    }}
                    error={!!errors.userName}
                    helperText={errors.userName?.message}
                  />
                </Col>
                <Col size={6}>
                  <Input
                    label="Email"
                    id="email"
                    placeholder="Enter your Email"
                    variant="primary"
                    type="email"
                    autoComplete="off"
                    required
                    value={watch('email')}
                    {...register('email', UPGRADE_FORM_VALIDATION.email)}
                    onChange={e => {
                      setValue('email', e.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    onBlur={() => {
                      trigger('email');
                    }}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                </Col>
              </Row>
              <Row gap="16px">
                <Col size={6}>
                  <Input
                    id="organizationName"
                    label="Organization Name"
                    placeholder="Enter your Organization Name"
                    required
                    variant="primary"
                    type="text"
                    value={watch('organizationName')}
                    {...register(
                      'organizationName',
                      UPGRADE_FORM_VALIDATION.organizationName,
                    )}
                    onChange={e => {
                      setValue('organizationName', e.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    onBlur={() => {
                      trigger('organizationName');
                    }}
                    error={!!errors.organizationName}
                    helperText={errors.organizationName?.message}
                  />
                </Col>
                <Col size={6}>
                  <Select
                    label="Job Title"
                    placeHolder="Please select an option"
                    required={false}
                    showToolTip
                    optionsList={optionsList}
                    selectedOption={{
                      label: watch('jobTitle'),
                      value: watch('jobTitle'),
                    }}
                    {...register('jobTitle')}
                    onChange={option => {
                      setValue('jobTitle', option.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      setIsOtherJobTitle(
                        option.value === 'Others' ? true : false,
                      );
                    }}
                    onBlur={() => trigger('jobTitle')}
                    errorMsg={errors.jobTitle?.message}
                    optionZIndex={3000}
                  />
                </Col>
              </Row>
              <Row gap="16px">
                {isOtherJobTitle && (
                  <Col size={6}>
                    <Input
                      id="others"
                      label="Other Job Title"
                      placeholder="Enter your Job Title"
                      variant="primary"
                      type="text"
                      required
                      value={watch('others')}
                      {...register('others')}
                      onChange={e =>
                        setValue('others', e.target.value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }
                      onBlur={() => trigger('others')}
                    />
                  </Col>
                )}
                <Col size={6}>
                  <PhoneInputField
                    id="phoneNumber"
                    country={countryCode.toLowerCase()}
                    defaultCountry={countryCode.toLowerCase()}
                    placeholder="Phone number"
                    international
                    value={watch('phoneNumber')}
                    {...register('phoneNumber')}
                    onChange={phone => {
                      setValue('phoneNumber', phone, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    onBlur={() => {
                      trigger('phoneNumber');
                    }}
                    isValid
                    dropdownStyle={{
                      height: '236px',
                      position: 'fixed',
                    }}
                  />
                </Col>
                <Col size={6}>
                  <Select
                    label="Topic"
                    placeHolder="Please select an topic"
                    required
                    showToolTip
                    optionsList={TopicList}
                    selectedOption={{
                      label: watch('topic'),
                      value: watch('topic'),
                    }}
                    {...register('topic')}
                    onChange={option => {
                      setValue('topic', option.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    onBlur={() => trigger('topic')}
                    errorMsg={errors.topic?.message}
                    optionZIndex={3000}
                  />
                </Col>
              </Row>
              <Row>
                <Col size={12}>
                  <TextArea
                    name="message"
                    label="Message"
                    placeholder="Type your message..."
                    capacity={200}
                    value={watch('message')}
                    onChange={e =>
                      setValue('message', e.target.value, { shouldDirty: true })
                    }
                  />
                </Col>
              </Row>
            </Container>
          </Drawer>
        );
      }}
    </Form>
  );
};
